import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { HubDataSource, HubItemCategoryModel } from "client/Api/Hub/hub";
import {
	ActiveFilter,
	HubItemIsNewFilter,
	listHubImageUrlWithAttributions,
	ImageUrlWithAttribution
} from "../../common";
import { DataSourceSortableState } from "client/Data/sorting";
import {
	EventDateFilterType,
	EventDetailsViewModel,
	EventPreviewModel,
	AttendanceErrorStatus,
	attendEvent,
	dontAttendEvent,
	get,
	registerInCalendar,
	UpdateEventInCalendarResult,
	EventAttendeeKind,
	AttendeePreviewModel,
	AttendeeCounter,
	registerView,
	getStatistics,
	EventStatisticsModel,
	SuccessfulUpdateEventAttendanceResultModel,
	UpdateEventAttendanceErrorModel,
	getReactions,
	getReactionCounts
} from "client/Api/Hub/Events/eventViewer";
import {
	AlertManager,
	useAlerts,
	useSynchronizedRef,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	Anchor,
	FormattedText,
	NoResults,
	TagModel,
	useConfirmationDialog
} from "client/Components";
import { TagViewModel } from "client/Api/Tags/tags";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNotNil
} from "client/Misc/utils";
import { ContentWithRelevant } from "../../ContentWithRelevant";
import { MediaContentModel } from "../../HubDetailsLayout";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { EditDialog } from "client/Features/Hub/Management/EventsManagement/EditEventDialog/EditEventDialog";
import { OtherEvents } from "./OtherEvents";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { never } from "client_legacy/ndaw/errors";
import {
	getHubImageUrl,
	HubCommentModel,
	HubLinkedDocumentViewModel,
	HubItemMediaKind
} from "client/Api/Hub/common";
import {
	HubCommentOptionsModel,
	HubItemMediaDetailsViewModel,
	HubItemViewLocation
} from "client/Api/Hub/common.details";
import { TimeZone } from "client/DateAndTime/timezones";
import { useImageViewer } from "../../useImageViewer";
import { HttpRequestError } from "client/Api/httpClient";
import {
	AttendOptions,
	EventContent,
	EventContentDataProps,
	EventContentModeProps,
	EventContentStatisticsProps
} from "./EventContent";
import { publish, HubEvent } from "../../events";
import { useViewersDialog } from "../../ViewersDialog/ViewersDialog";
import { CommentThread } from "../../HubCommentSection/CommentThread";
import { HubItemCommentSection } from "../../HubItemCommentSection/HubItemCommentSection";
import styles from "./Details.module.less";
import { MobileCommentBoxPreservedSpace } from "../../HubCommentSection/MobileComment/MobileCommentBoxPreservedSpace";
import { getRelatedCommentThread } from "client/Api/Hub/Events/eventComments";
import { HubCommentSection, HubCommentSectionService } from "../../HubCommentSection/HubCommentSection";
import { getEventCommentService } from "./getEventCommentService";
import { useViewSpecificHandlers } from "../../useViewSpecificHandlers";
import { useReactionsDialog } from "client/Features/Reactions/ReactionsDialog/ReactionsDialog";

type DetailsModeProps = ConditionalProps<
	{
		selectedCategoryId: number | null;
		activeFilter: ActiveFilter | null;
		onActiveFilterChange(filter: ActiveFilter): void;
		searchTerm: VersionedValue<string>;
		sorting: DataSourceSortableState<EventPreviewModel>;
		dateFilter: EventDateFilterType;
		onBack: () => void;
		onNavigateToItem(id: number): void;
		onCategoryCreated: () => void;
		isNewFilter: HubItemIsNewFilter;
	},
	"mode",
	"full",
	"details-only"
>;

export type DetailsProps = {
	id: number;
	getCategory: (categoryId: number | nil) => HubItemCategoryModel | undefined;
	viewLocation: HubItemViewLocation;
	onLoadingError?: (error: unknown) => TryResult<void>;
	onSelectCategory: (categoryId: number) => void;
	deepLink?: EventDetailsDeepLink;
}
	& DetailsModeProps;

export const Details = (props: DetailsProps): ReactElement => {
	const {
		id,
		viewLocation,
		mode,
		activeFilter,
		getCategory,
		onActiveFilterChange,
		onBack,
		onCategoryCreated,
		onSelectCategory,
		onLoadingError,
		deepLink,
		selectedCategoryId
	} = props;

	const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("loading");
	const [item, setItem] = useState<EventDetailsViewModel | null>(null);

	const [state, setState] = useState<DetailsState>({
		currentUserAttendanceKind: null,
		isBeforeSignUpDate: false,
		isBeforeEventStartDate: false,
		attendeeCounts: [],
		maxAttendees: 0,
		signUpDeadline: null,
		previewAttendees: []
	});

	const [idToEdit, setIdToEdit] = useState<number | null>(null);
	const [statistics, setStatistics] = useState<EventStatisticsModel>();
	const [commentOptions, setCommentOptions] = useState<HubCommentOptionsModel | null>(null);
	const [isEditingMobileComment, setIsEditingMobileComment] = useState<boolean>(false);

	const [commentsState, setCommentsState] = useState<CommentsState>(
		(): CommentsState => deepLink?.kind === "comment"
			? {
				kind: "comment-thread",
				commentId: deepLink.commentId
			}
			: {
				kind: "all-comments",
				shouldReserveSpace: false
			}
	);

	const {
		currentUserAttendanceKind,
		isBeforeSignUpDate,
		isBeforeEventStartDate,
		attendeeCounts,
		signUpDeadline,
		maxAttendees
	} = state;

	const onLoadingErrorRef: ReadonlyRefObject<((error: unknown) => TryResult<void>) | undefined> = useSynchronizedRef(onLoadingError);
	const viewLocationRef: ReadonlyRefObject<HubItemViewLocation> = useSynchronizedRef(viewLocation);
	const linkedDocuments: readonly HubLinkedDocumentViewModel[] = item?.linkedDocuments
		?? EMPTY_ARRAY;

	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [viewersDialog, openViewersDialog] = useViewersDialog();
	const [reactionsDialog, openReactionsDialog] = useReactionsDialog();
	const [confirmationDialogElement, openConfirmationDialog] = useConfirmationDialog();

	useViewSpecificHandlers({
		isEditingMobileComment,
		openConfirmationDialog,
		onBack: mode === "full"
			? onBack
			: undefined
	});

	const imageViewerItems = useMemo(
		(): readonly ImageUrlWithAttribution[] => {
			return listHubImageUrlWithAttributions(HubDataSource.Event, item);
		},
		[item]
	);

	const [imageViewerDialog, viewImageHandler] = useImageViewer(imageViewerItems);

	const registerViewAndFetchStatisticsHandler = useCallback(
		async (itemId: number, signal: AbortSignal): Promise<void> => {
			try {
				await registerView(
					itemId,
					{ location: viewLocationRef.current },
					signal
				);

				const statistics: EventStatisticsModel = await getStatistics(itemId, signal);
				setStatistics(statistics);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}
		},
		[viewLocationRef]
	);

	const reloadStatistics = useCallback(
		async (): Promise<void> => {
			try {
				const statistics: EventStatisticsModel = await getStatistics(id, globalCancellation);
				setStatistics(statistics);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}
		},
		[globalCancellation, id]
	);

	const allCommentViewHandler = useCallback(
		(): void => {
			setCommentsState({
				kind: "all-comments",
				shouldReserveSpace: true
			});
		},
		[]
	);

	const getRelatedCommentThreadHandler = useCallback(
		(commentId: number, signal?: AbortSignal): Promise<readonly HubCommentModel[]> => {
			return getRelatedCommentThread(
				id,
				commentId,
				signal
			);
		},
		[id]
	);

	const fetchItem = useCallback(
		async (
			id: number,
			signal: AbortSignal,
			isFirstLoad: boolean = false
		): Promise<void> => {
			setItem(null);
			setLoadingStatus("loading");

			try {
				const {
					event,
					commentOptions
				} = await get(id, signal);

				const {
					id: itemId,
					isBeforeSignUpDate,
					isBeforeEventStartDate,
					availableSpots,
					attendeeCounts,
					signUpDeadline,
					previewAttendees,
					attendeeKind,
					isNew,
					categoryId
				} = event;

				setState({
					currentUserAttendanceKind: attendeeKind,
					isBeforeSignUpDate,
					isBeforeEventStartDate,
					attendeeCounts,
					maxAttendees: availableSpots,
					signUpDeadline,
					previewAttendees
				});

				setItem(event);
				setCommentOptions(commentOptions);
				setLoadingStatus("found");

				if (isFirstLoad) {
					if (isNew) {
						publish(
							HubEvent.IsNewStatusCleared,
							{
								kind: HubDataSource.Event,
								itemId,
								categoryId
							}
						);
					}

					registerViewAndFetchStatisticsHandler(itemId, signal);
				} else {
					reloadStatistics();
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				let loadingStatus: LoadingStatus = "error";

				if (e instanceof HttpRequestError && e.statusCode === 404) {
					loadingStatus = "not-found";
				}

				setLoadingStatus(loadingStatus);

				const { current: onLoadingError } = onLoadingErrorRef;

				const wasHandled: boolean = onLoadingError?.(e)
					?.wasSuccessful ?? false;

				if (!wasHandled) {
					console.error(e);
					alertManager.showError(localize("_CannotOpenItem"));
				}
			}
		},
		[
			alertManager,
			onLoadingErrorRef,
			registerViewAndFetchStatisticsHandler,
			reloadStatistics
		]
	);

	useEffect(
		() => {
			const cancellation = new AbortController();
			fetchItem(
				id,
				cancellation.signal,
				true
			);

			return () => cancellation.abort();
		},
		[
			alertManager,
			fetchItem,
			id
		]
	);

	const tags = useMemo(
		(): readonly TagModel<number>[] => {
			return item?.tags
				.map((x): TagModel<number> => ({
					text: x.name,
					value: x.id,
					color: "secondary"
				})) ?? [];
		},
		[item?.tags]
	);

	const afterSaveHandler = useCallback(
		(): void => {
			fetchItem(id, globalCancellation);
		},
		[
			id,
			globalCancellation,
			fetchItem
		]
	);

	const editHandler = useCallback(
		(): void => {
			setIdToEdit(
				ensureNotNil(item?.id)
			);
		},
		[item?.id]
	);

	const closeEditDialogHandler = useMemo(
		(): ((wasAnyCategoryCreated: boolean) => void) | undefined => {
			if (!onCategoryCreated) {
				return undefined;
			}

			return (wasAnyCategoryCreated): void => {
				setIdToEdit(null);

				if (wasAnyCategoryCreated) {
					onCategoryCreated();
				}
			};
		},
		[onCategoryCreated]
	);

	const processRegisterInCalendarResult = useCallback(
		(result: UpdateEventInCalendarResult, retryHandler: () => void): void => {
			switch (result) {
				case UpdateEventInCalendarResult.NotUpdated:
					break;
				case UpdateEventInCalendarResult.Success:
					alertManager.showSuccess(localize("_EventIsAddedToYourCalendar"));
					break;
				case UpdateEventInCalendarResult.Unknown:
				case UpdateEventInCalendarResult.Failure:
					alertManager.showWarning(
						<FormattedText
							text={localize("_FailedToAddThisEventToYourCalendarClickHereToTryAgain")}
							thingsToReplace={[
								{
									what: "{{Here}}",
									filler: (
										<Anchor onClick={retryHandler}>
											{localize("_Here")}
										</Anchor>
									)
								}
							]}
						/>
					);
					break;
				default:
					never(result);
			}
		},
		[alertManager]
	);

	const addToCalendarHandler = useCallback(
		async (): Promise<void> => {
			try {
				const result: UpdateEventInCalendarResult = await registerInCalendar(id, globalCancellation);
				processRegisterInCalendarResult(result, addToCalendarHandler);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				processRegisterInCalendarResult(
					UpdateEventInCalendarResult.Failure,
					addToCalendarHandler
				);
			}
		},
		[
			globalCancellation,
			processRegisterInCalendarResult,
			id
		]
	);

	const attendHandler = useCallback(
		async (options: AttendOptions): Promise<void> => {
			try {
				setLoadingStatus("loading");
				let result: SuccessfulUpdateEventAttendanceResultModel;

				if (options.isAttending) {
					const attendResult: SuccessfulUpdateEventAttendanceResultModel = await attendEvent(
						id,
						options.kind,
						globalCancellation
					);

					if (!options.wasAttending) {
						processRegisterInCalendarResult(
							attendResult.updateEventInCalendarResult,
							addToCalendarHandler
						);
					}

					result = attendResult;
				} else {
					result = await dontAttendEvent(
						id,
						globalCancellation
					);
				}

				const {
					attendeeKind,
					isBeforeSignUpDeadline,
					isBeforeEventStartDate,
					attendeeCounts,
					maxAttendees,
					signUpDeadline,
					previewAttendees
				} = result.attendeesInformation;

				setState(prevState => ({
					currentUserAttendanceKind: attendeeKind,
					isBeforeSignUpDate: isBeforeSignUpDeadline,
					isBeforeEventStartDate,
					attendeeCounts,
					maxAttendees,
					signUpDeadline: signUpDeadline || prevState.signUpDeadline,
					previewAttendees
				}));

				alertManager.showSuccess(
					<FormattedText
						text={localize(
							isNotNil(attendeeKind)
								? attendeeKind === EventAttendeeKind.Online
									? "_YouAreAttendingTheEventTitleOnline"
									: "_YouAreAttendingTheEventTitlePhysically"
								: "_YouAreNotAttendingTheEventTitleAnymore"
						)}
						thingsToReplace={[
							{
								what: "{{Title}}",
								filler: item?.title
							}
						]}
					/>
				);

				setLoadingStatus("found");
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				let errorKey: LocalizationKey | undefined;

				if ((e instanceof HttpRequestError) && (e.statusCode === 409)) {
					try {
						const errorModel: UpdateEventAttendanceErrorModel = e.readJson<UpdateEventAttendanceErrorModel>();
						errorKey = attendanceErrorStatusToErrorKeyMap[errorModel.status];
					} catch (e) {
						console.error(e);
					}
				}

				console.error(e);
				alertManager.showError(localize(errorKey ?? "_GeneralErrorMessage"));
				setLoadingStatus("found");
			}
		},
		[
			id,
			globalCancellation,
			processRegisterInCalendarResult,
			addToCalendarHandler,
			alertManager,
			item?.title
		]
	);

	const eventCommentService = useMemo(
		(): HubCommentSectionService => {
			return getEventCommentService(id);
		},
		[id]
	);

	const dataProps = useMemo(
		(): EventContentDataProps => {
			if (!item) {
				return {
					hasData: false
				};
			}

			return {
				hasData: true,
				title: item.title,
				createdBy: item.author,
				isEditable: item.isEditable,
				category: getCategory(item.categoryId),
				selectedCategoryId,
				media: convertMedia(item.media),
				layout: item.layout,
				startDate: item.startDate,
				endDate: item.endDate,
				publicationDate: item.publicationDate,
				externalUrl: item.externalUrl,
				lastModifiedDate: item.lastModifiedDate,
				lastModifiedUser: item.lastModifiedUser,
				tags: tags,
				location: item.location,
				locationType: item.locationType,
				onlineEventUrl: item.onlineEventUrl,
				signUpDeadline: signUpDeadline,
				isBeforeSignUpDate: isBeforeSignUpDate,
				isBeforeEventStartDate: isBeforeEventStartDate,
				ticketType: item.ticketType,
				isAttendanceEnabled: item.isAttendanceEnabled,
				availableSpots: maxAttendees,
				attendeeCounts: attendeeCounts,
				previewAttendees: item.previewAttendees,
				areCommentsEnabled: item.areCommentsEnabled
			};
		},
		[
			attendeeCounts,
			getCategory,
			isBeforeEventStartDate,
			isBeforeSignUpDate,
			item,
			maxAttendees,
			selectedCategoryId,
			signUpDeadline,
			tags
		]
	);

	const isAllowedToLookUpViewers: boolean = isNotNil(item)
		&& item.isEditable;

	const statisticsProps = useMemo(
		(): EventContentStatisticsProps => {
			if (!statistics) {
				return {
					hasStatistics: false
				};
			}

			return {
				hasStatistics: true,
				statistics,
				onOpenViewersDialog: isAllowedToLookUpViewers
					? () => openViewersDialog({
						count: statistics.viewsCount,
						itemId: id,
						itemKind: HubItemKind.Event
					})
					: undefined,
				onOpenReactionsDialog: () => openReactionsDialog({
					itemId: id,
					getReactions: getReactions,
					getReactionCounts: getReactionCounts
				}),
				onStatisticsRetrieved: setStatistics
			};
		},
		[
			id,
			isAllowedToLookUpViewers,
			openViewersDialog,
			statistics,
			openReactionsDialog
		]
	);

	const modeProps = useMemo(
		(): EventContentModeProps => {
			switch (mode) {
				case "full":
					return {
						mode: "full",
						activeFilter,
						onBack,
						onEdit: editHandler,
						onSelectAuthor: (authorId, authorName): void => {
							onActiveFilterChange({
								kind: "author",
								value: {
									id: authorId,
									name: authorName
								}
							});
						},
						onSelectCategory,
						onTagClick: (id: number | null): void => {
							const tag: TagViewModel = ensureNotNil(
								item?.tags
									.find(x => x.id === id)
							);

							onActiveFilterChange({
								kind: "tag",
								value: {
									...tag,
									itemKind: HubItemKind.Event
								}
							});
						}
					};
				case "details-only":
					return {
						mode: "details-only",
						onSelectCategory
					};
				default:
					never(mode);
			}
		},
		[
			activeFilter,
			editHandler,
			item?.tags,
			mode,
			onActiveFilterChange,
			onBack,
			onSelectCategory
		]
	);

	const commentsCount: number = statistics?.commentsCount
		.isSet
		? statistics.commentsCount
			.value
		: 0;

	const renderCommentsSection = (): ReactElement | null => {
		if (!item) {
			return null;
		}

		if (!item.areCommentsEnabled) {
			return (
				<HubCommentSection.NoComments />
			);
		}

		return isNotNil(commentOptions)
			? (
				<div className={styles.commentsSectionContainer}>
					{commentsState.kind === "comment-thread"
						? (
							<CommentThread
								commentId={commentsState.commentId}
								onStatisticsUpdate={reloadStatistics}
								onMobileCommentIsEditingChanged={setIsEditingMobileComment}
								options={commentOptions}
								timezone={userTimezone}
								onAllCommentView={allCommentViewHandler}
								allCommentsCount={commentsCount}
								getRelatedCommentThread={getRelatedCommentThreadHandler}
								service={eventCommentService}
							/>
						)
						: (
							<div
								className={
									commentsState.shouldReserveSpace
										? styles.withMinHeight
										: undefined
								}
							>
								<HubItemCommentSection
									kind={HubItemKind.Event}
									onStatisticsUpdate={reloadStatistics}
									onMobileCommentIsEditingChanged={setIsEditingMobileComment}
									timezone={userTimezone}
									options={commentOptions}
									allCommentsCount={commentsCount}
									service={eventCommentService}
								/>
							</div>
						)
					}
				</div>
			)
			: null;
	};

	return (
		<>
			<ContentWithRelevant trackingId="details">
				<ContentWithRelevant.Content hasBusyIndicator={loadingStatus === "loading"}>
					<EventContent
						{...dataProps}
						{...statisticsProps}
						{...modeProps}
						isInteractive
						id={id}
						attendeeKind={currentUserAttendanceKind}
						onAttend={attendHandler}
						onAddToCalendar={addToCalendarHandler}
						linkedDocumentsMode="view"
						linkedDocuments={linkedDocuments}
						onChangeIsLoading={(isLoading) => setLoadingStatus(isLoading ? "loading" : "found")}
						timezone={userTimezone}
						onViewImage={viewImageHandler}
					/>

					{renderCommentsSection()}

					{loadingStatus === "not-found" && (
						<NoResults
							{...(onBack
								? {
									retryText: localize("_BackToOverview"),
									onRetry: onBack
								}
								: {}
							)}
							icon="calendar-star"
							title={localize("_ItemNotFound")}
							message={localize("_TheItemMayHaveBeenDeletedOrYouDontHaveThePermissionsToReadIt")}
							trackingId="event-not-found"
							height="fill"
						/>
					)}
				</ContentWithRelevant.Content>

				{(props.mode === "full" && loadingStatus !== "not-found") && (
					<ContentWithRelevant.Relevant title={localize("_OtherEvents")}>
						<OtherEvents
							item={item}
							getCategory={getCategory}
							activeFilter={props.activeFilter}
							onActiveFilterChange={props.onActiveFilterChange}
							selectedCategoryId={props.selectedCategoryId}
							onSelectedCategoryIdChange={props.onSelectCategory}
							searchTerm={props.searchTerm}
							sorting={props.sorting}
							dateFilter={props.dateFilter}
							onNavigateToItem={props.onNavigateToItem}
							timezone={userTimezone}
							isNewFilter={props.isNewFilter}
						/>
					</ContentWithRelevant.Relevant>
				)}
				{item?.areCommentsEnabled && (
					<MobileCommentBoxPreservedSpace />
				)}
				{viewLocation === HubItemViewLocation.Hub && (
					<AiAssistantFabSpacing />
				)}
			</ContentWithRelevant>

			{imageViewerDialog}
			{viewersDialog}
			{confirmationDialogElement}
			{reactionsDialog}

			{isNotNil(idToEdit) && isNotNil(closeEditDialogHandler) && (
				<EditDialog
					mode={{
						kind: "edit",
						eventId: idToEdit
					}}
					onSave={afterSaveHandler}
					onClose={closeEditDialogHandler}
					onBack={closeEditDialogHandler}
				/>
			)}
		</>
	);
};
Details.displayName = "Details";

export type EventDetailsDeepLink = {
	kind: "comment";
	commentId: number;
} | {
	kind: "comments";
};

type LoadingStatus =
	| "loading"
	| "found"
	| "not-found"
	| "error";

type CommentsState = {
	kind: "all-comments";
	shouldReserveSpace?: boolean;
} | {
	kind: "comment-thread";
	commentId: number;
};

function convertMedia(media: HubItemMediaDetailsViewModel): MediaContentModel {
	switch (media.mediaKind) {
		case HubItemMediaKind.Image:
			return {
				mediaKind: HubItemMediaKind.Image,
				imageUrl: getHubImageUrl(HubDataSource.Event, media.imageId),
				attribution: media.attribution
			};
		default:
			return media;
	}
}

type DetailsState = {
	currentUserAttendanceKind: EventAttendeeKind | null;
	isBeforeSignUpDate: boolean;
	isBeforeEventStartDate: boolean;
	attendeeCounts: readonly AttendeeCounter[];
	maxAttendees: number | null;
	signUpDeadline: Date | null;
	previewAttendees: readonly AttendeePreviewModel[];
};

const attendanceErrorStatusToErrorKeyMap: Record<AttendanceErrorStatus, LocalizationKey> = {
	[AttendanceErrorStatus.NoSpotsAvailable]: "_AllAvailableSpotsAreTaken",
	[AttendanceErrorStatus.SignUpDeadlineHasPassed]: "_SignUpDateHasPassedMessage",
	[AttendanceErrorStatus.InvalidOperationForUser]: "_GeneralErrorMessage",
	[AttendanceErrorStatus.EventHasStarted]: "_YouCantCancelYourAttendenceOnceTheEventHasStarted"
};
